import { Alert, Button, SlackLogo, Typography } from '@compass/components'
import { Route } from '@routes/_authenticated/_layout/workspaces/$subscriptionId/bot-detection/index'
import { AppRoute } from 'appRoutes'
import { LinkButtonLink } from 'components/compass-integration'
import { AppliedQueryFilters, QueryFilterChangeHandler, QueryFilterMenu } from 'components/QueryFilters'
import { DOCS_AUTOMATION_INTELLIGENCE_API } from 'const'
import {
  DateRangeContextProvider,
  PersistentDateRangePicker,
  ProductFeedbackDialog,
  SetHeader,
  SubHeader,
  useDateRangeContext,
  useDefaultPredefinedRanges,
} from 'features/commonUI'
import { isPredefinedRange } from 'helpers/dateRange'
import { useLogPageView } from 'helpers/vendor'
import { useCompassToast } from 'hooks/toast'
import { useBotDetectionAccess } from 'hooks/useBotDetectionAccess'
import { ExternalLinkIcon, FlaskConicalIcon, Link as LinkIcon, PlusIcon } from 'lucide-react'
import { DateTime, Duration } from 'luxon'
import { ampli } from 'models/ampli'
import { useCallback, useMemo, useState } from 'react'

import { BOT_DETECTION_MAX_RANGE_DAYS, BOT_DETECTION_PREDEFINED_DATE_RANGE_IDS } from '../dateRanges'
import {
  useBotDetectionCategoryIdentityMatrix,
  useBotDetectionDistributions,
  useBotDetectionOverviewTimeline,
  useBotDetectionStats,
} from '../hooks'
import {
  BotDetectionSource,
  BotDetectionStatsResponse,
  BotDetectionTimelineChartId,
  BotDetectionTimelinePeriod,
} from '../models'
import { BotDetectionDistributions } from './BotDetectionDistributions'
import { BotDetectionIdentityMatrix } from './BotDetectionIdentityMatrix'
import { BotDetectionMetricCards } from './BotDetectionMetricCards'
import { BotDetectionOverviewSkeleton } from './BotDetectionOverviewSkeleton'
import { BotDetectionSourceTabs } from './BotDetectionSourceTabs'
import { BotDetectionTimelineChart } from './BotDetectionTimelineChart'
import { BotDetectionSourceUpsell } from './BotDetectionUpsellModal'
import {
  BotDetectionOverviewFilterValues,
  getActiveBotDetectionOverviewFilters,
  getAppliedBotDetectionOverviewFilterCount,
  getClearedBotDetectionOverviewFilters,
  useBotDetectionOverviewFilter,
} from './useBotDetectionOverviewFilter'
import { copyCurrentUrlToClipboard } from './utils'

const EMPTY_STATS = {
  period: { from: '', to: '' },
  metrics: Object.fromEntries(
    [
      'totalEvents',
      'totalDetections',
      'botRate',
      'aiTrafficShare',
      'trustedBotShare',
      'spoofedBotCount',
      'unknownBotCount',
    ].map((id) => [id, { value: 0, delta: null, deltaPercent: null, trend: null }])
  ),
} as BotDetectionStatsResponse

export function BotDetectionOverviewPage() {
  return (
    <DateRangeContextProvider>
      <Page />
    </DateRangeContextProvider>
  )
}

function Page() {
  const { subscriptionId } = Route.useParams()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const { dateRange, timeZone } = useDateRangeContext()
  const { ranges } = useDefaultPredefinedRanges(BOT_DETECTION_PREDEFINED_DATE_RANGE_IDS)
  const filterValues = getActiveBotDetectionOverviewFilters(search)
  const source: BotDetectionSource = search.source ?? 'edge'
  const timelineStartDate = dateRange?.startDate
  const timelineEndDate = dateRange?.endDate
  const botDetectionAccess = useBotDetectionAccess()

  const hasSourceAccess =
    source === 'edge' ? botDetectionAccess.hasAiApiAccess : botDetectionAccess.hasBrowserBotsAccess
  // Entitlement is per source, so the upsell follows the selected tab rather than the whole page.
  // While access is still resolving we show neither data nor upsell.
  const showSourceUpsell = !botDetectionAccess.isLoading && !hasSourceAccess
  const identificationStatsQuery = useBotDetectionStats({
    subscriptionId,
    since: timelineStartDate,
    before: timelineEndDate,
    source: 'identification',
    enabled: botDetectionAccess.hasBrowserBotsAccess,
    ...filterValues,
  })
  const edgeStatsQuery = useBotDetectionStats({
    subscriptionId,
    since: timelineStartDate,
    before: timelineEndDate,
    source: 'edge',
    enabled: botDetectionAccess.hasAiApiAccess,
    ...filterValues,
  })
  const statsQuery = source === 'identification' ? identificationStatsQuery : edgeStatsQuery
  const sourceCounts: Record<BotDetectionSource, number | undefined> = {
    identification: identificationStatsQuery.data?.metrics.totalDetections.value,
    edge: edgeStatsQuery.data?.metrics.totalDetections.value,
  }
  const selectedChart = search.selectedChart ?? 'totalDetections'
  const timelineGranularity: BotDetectionTimelinePeriod = search.timelineGranularity ?? 'day'
  const timelineQuery = useBotDetectionOverviewTimeline({
    subscriptionId,
    since: timelineStartDate,
    before: timelineEndDate,
    period: timelineGranularity,
    chart: selectedChart,
    source,
    enabled: hasSourceAccess,
    ...filterValues,
  })
  const distributionsQuery = useBotDetectionDistributions({
    subscriptionId,
    since: timelineStartDate,
    before: timelineEndDate,
    source,
    enabled: hasSourceAccess,
    ...filterValues,
  })
  const exportTotalDetections =
    statsQuery.isPlaceholderData || distributionsQuery.isPlaceholderData
      ? undefined
      : statsQuery.data?.metrics.totalDetections.value
  const matrixQuery = useBotDetectionCategoryIdentityMatrix({
    subscriptionId,
    since: timelineStartDate,
    before: timelineEndDate,
    source,
    enabled: hasSourceAccess,
    ...filterValues,
  })
  const { filterDefinitions, filterMenuLayout } = useBotDetectionOverviewFilter({
    subscriptionId,
    filterValues,
    botNames: distributionsQuery.data?.botNames,
  })
  const appliedFilterCount = getAppliedBotDetectionOverviewFilterCount(filterValues)
  const handleSelectChart = useCallback(
    (chartId: BotDetectionTimelineChartId) => {
      navigate({ search: (old) => ({ ...old, selectedChart: chartId }) })
    },
    [navigate]
  )
  const eventsLink = useMemo(() => {
    const aggregateStart = timelineStartDate?.startOf('hour')
    const flooredEnd = timelineEndDate?.startOf('hour')
    const aggregateEnd =
      aggregateStart && flooredEnd && flooredEnd <= aggregateStart ? aggregateStart.plus({ hours: 1 }) : flooredEnd

    return {
      subscriptionId,
      source,
      since: aggregateStart?.toISO() ?? undefined,
      before: aggregateEnd?.toISO() ?? undefined,
      period: search.period,
      filterValues,
    }
  }, [filterValues, search.period, source, subscriptionId, timelineEndDate, timelineStartDate])
  const definedRanges = useMemo(
    () => BOT_DETECTION_PREDEFINED_DATE_RANGE_IDS.map((rangeId) => ranges[rangeId]),
    [ranges]
  )
  const handleFilterChange = useCallback<QueryFilterChangeHandler<BotDetectionOverviewFilterValues>>(
    (nextFilters) => {
      ampli.attributeFilterApplied({
        datasetFocus: 'Bot Detection',
        attributeFilters: Object.keys(nextFilters),
        filterType: 'Dropdown',
      })
      navigate({
        search: (old) => ({ ...old, ...nextFilters }),
      })
    },
    [navigate]
  )
  const handleClearFilters = useCallback(() => {
    navigate({
      search: (old) => ({
        ...old,
        ...getClearedBotDetectionOverviewFilters(filterDefinitions),
      }),
    })
  }, [filterDefinitions, navigate])
  const handleTimelineGranularityChange = useCallback(
    (granularity: BotDetectionTimelinePeriod) => {
      navigate({
        search: (old) => ({ ...old, timelineGranularity: granularity }),
      })
    },
    [navigate]
  )
  const handleSourceChange = useCallback(
    (nextSource: BotDetectionSource) => {
      navigate({ search: (old) => ({ ...old, source: nextSource }) })
    },
    [navigate]
  )

  useLogPageView(() =>
    ampli.dataWithFiltersAppliedViewed({
      datasetFocus: 'Bot Detection',
      'App Page Path': AppRoute.BotDetection,
      periodLengthDays: Math.round(
        timelineEndDate && timelineStartDate ? timelineEndDate.diff(timelineStartDate, 'days').days : 0
      ),
    })
  )

  return (
    <>
      <SetHeader
        title='Overview'
        breadcrumb={[
          {
            title: 'Bots & AI agents',
            to: AppRoute.BotDetection,
            params: { subscriptionId },
          },
        ]}
      />
      <SubHeader
        keepRowLayout
        title={
          <Typography variant='heading' size='2xl' as='h2'>
            Overview
          </Typography>
        }
        description={
          <Typography size='base' className='text-gray-800'>
            Analyze bot traffic trends across your workspace.
          </Typography>
        }
        actions={
          <>
            <LinkButtonLink to={AppRoute.SubmitBot} params={{ subscriptionId }} variant='secondary'>
              <PlusIcon size={16} />
              Submit bot
            </LinkButtonLink>
            <CopyLinkButton />
          </>
        }
      />
      <AutomationIntelligencePreviewAlert />
      <div data-testid='bot-detection-overview-page' className='flex flex-col gap-4'>
        <div className='flex flex-col md:flex-row md:items-center md:justify-between gap-3'>
          <div className='flex flex-col sm:flex-row sm:items-center gap-3'>
            <QueryFilterMenu
              appliedFilterCount={appliedFilterCount}
              filterMenuLayout={filterMenuLayout}
              filterDefinitions={filterDefinitions}
              filterValues={filterValues}
              onChange={handleFilterChange}
              onOpen={() => ampli.attributeFilterDialogOpened({ datasetFocus: 'Bot Detection' })}
              filterItemTestId='bot-detection-overview-query-filter-item'
              renderOptionsAsSubmenu
            />
            <PersistentDateRangePicker
              timezone={timeZone}
              route={AppRoute.BotDetection}
              routeParams={{ subscriptionId }}
              definedRanges={definedRanges}
              defaultDateRange={ranges.today}
              initialMinDate={DateTime.now().minus({ days: BOT_DETECTION_MAX_RANGE_DAYS }).startOf('day')}
              initialMaxDate={DateTime.now().endOf('day')}
              maxRange={Duration.fromDurationLike({ days: BOT_DETECTION_MAX_RANGE_DAYS })}
              onOpen={() => ampli.dateFilterSelectorOpened({ datasetFocus: 'Bot Detection' })}
              onApplyRange={(range) => {
                ampli.dateFilterApplied({
                  datasetFocus: 'Bot Detection',
                  dateRange: isPredefinedRange(range) ? (range.label as any) : 'Other',
                  periodLengthDays: Math.round(range.endDate.diff(range.startDate, 'days').days),
                })
              }}
            />
            <BotDetectionSourceTabs value={source} counts={sourceCounts} onChange={handleSourceChange} />
          </div>
          {appliedFilterCount > 0 && (
            <Button variant='ghost' tone='background2' alt onPress={handleClearFilters} aria-label='Clear filters'>
              Clear
            </Button>
          )}
        </div>
        <AppliedQueryFilters
          dataTestId='bot-detection-overview-applied-filters'
          filterDefinitions={filterDefinitions}
          filterValues={filterValues}
          onChange={handleFilterChange}
          filterItemTestId='bot-detection-overview-query-filter-item'
        />
        {showSourceUpsell ? (
          <div className='relative'>
            <BotDetectionOverviewSkeleton showControls={false} />
            <BotDetectionSourceUpsell
              subscriptionId={subscriptionId}
              source={source}
              gradientClass='bg-linear-to-b to-gray-100'
            />
          </div>
        ) : (
          <>
            <div className='flex flex-col overflow-hidden gap-px bg-gray-300 border rounded-lg'>
              <div className='flex overflow-x-auto overscroll-x-none gap-px lg:grid lg:grid-cols-3'>
                <BotDetectionMetricCards
                  stats={hasSourceAccess ? statsQuery.data : EMPTY_STATS}
                  isLoading={hasSourceAccess && statsQuery.isLoading}
                  selectedChart={selectedChart}
                  onSelectChart={handleSelectChart}
                  eventsLink={eventsLink}
                />
              </div>
              <BotDetectionTimelineChart
                data={timelineQuery.data}
                chart={selectedChart}
                granularity={timelineGranularity}
                isLoading={timelineQuery.isLoading}
                error={timelineQuery.error}
                onChartChange={handleSelectChart}
                onGranularityChange={handleTimelineGranularityChange}
                filtersApplied={appliedFilterCount > 0}
              />
            </div>
            <BotDetectionDistributions
              distributions={distributionsQuery.data}
              totalDetections={exportTotalDetections}
              isLoading={hasSourceAccess && distributionsQuery.isLoading}
              isError={distributionsQuery.isError}
              subscriptionId={subscriptionId}
              since={eventsLink.since}
              before={eventsLink.before}
              period={search.period}
              filterValues={filterValues}
              source={source}
              filtersApplied={appliedFilterCount > 0}
              from={timelineStartDate}
              to={timelineEndDate}
            />
            <BotDetectionIdentityMatrix
              matrix={matrixQuery.data}
              isLoading={hasSourceAccess && matrixQuery.isLoading}
              error={matrixQuery.error}
              subscriptionId={subscriptionId}
              since={eventsLink.since}
              before={eventsLink.before}
              period={search.period}
              filterValues={filterValues}
              source={source}
            />
          </>
        )}
      </div>
    </>
  )
}

function AutomationIntelligencePreviewAlert() {
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false)

  return (
    <>
      <Alert
        variant='primary'
        icon={<FlaskConicalIcon />}
        actionProps={[
          {
            children: (
              <>
                <SlackLogo /> Share feedback
              </>
            ),
            onPress: () => setIsFeedbackDialogOpen(true),
          },
          {
            children: (
              <>
                Learn more <ExternalLinkIcon />
              </>
            ),
            href: DOCS_AUTOMATION_INTELLIGENCE_API,
            target: '_blank',
          },
        ]}
      >
        To detect AI agents at the edge, use the new <b>Automation Intelligence API</b>, now in free public preview.
      </Alert>
      <ProductFeedbackDialog dialogOpen={isFeedbackDialogOpen} setDialogOpen={setIsFeedbackDialogOpen} />
    </>
  )
}

function CopyLinkButton() {
  const { showToast } = useCompassToast()

  const handleCopyLink = () => copyCurrentUrlToClipboard(showToast)

  return (
    <Button
      variant='secondary'
      aria-label='Copy link'
      tooltip='Copy the URL with filters applied. Only your team can access this link.'
      onPress={handleCopyLink}
    >
      <LinkIcon size={16} />
      Copy link
    </Button>
  )
}
